<script setup lang="ts">
import type { ReviewsQueryParameters, VehicleTypeInteger } from '@yescapa-dev/ysc-api-js/legacy'
import { YSC_API_REVIEW_ERROR } from '~/utils/error/YscErrorClasses'

const { $api } = useYscApi()
const { localeProperties: { yscCode } } = useLocaleProperties()

const props = defineProps({
  vehicleType: {
    type: Number as PropType<VehicleTypeInteger>,
    default: null,
  },
})

const { data: reviews, error } = await useAsyncData('component-app-review-testimonial', async () => {
  const params: ReviewsQueryParameters = {
    language: yscCode,
    page_size: 3,
    has_photos: true,
    is_published: true,
    allowed_on_home: true,
    avg__gte: 4,
  }
  if (props.vehicleType) {
    params.vehicle_type = props.vehicleType
  }
  return await $api.reviews.getAll(params)
})

if (error.value) {
  const { $errorManager } = useErrorManager()
  $errorManager({ e: error.value, name: YSC_API_REVIEW_ERROR })
  throw createError({ statusCode: 404, statusMessage: error.value.message })
}
</script>

<template>
  <div class="flex flex-nowrap overflow-x-auto">
    <div
      v-if="reviews"
      class="flex flex-nowrap items-stretch space-x-8"
    >
      <div
        v-for="(review, index) in reviews.results"
        :key="index"
        class="min-w-[300px] flex-1 overflow-hidden rounded bg-gray-100"
      >
        <TwicImg
          :src="twicpicsPath(review.photos[0].path)"
          focus="auto"
          ratio="16/9"
          alt="-"
        />
        <div class="space-y-4 p-4">
          <div class="flex flex-wrap items-center space-x-4">
            <YscAvatar
              :first-name="review.author.first_name"
              :picture="review.author.photo || undefined"
              radius="20"
            />
            <div>
              <p class="font-semibold">
                {{ review.author.first_name }}
              </p>
              <p
                v-if="review.published_date"
                class="italic text-gray-500"
              >
                {{ $d(new Date(review.published_date), 'Y.numeric M.long D.numeric') }}
              </p>
            </div>
          </div>
          <div
            class="relative line-clamp-4 pl-8 before:absolute before:left-0 before:text-6xl before:leading-none before:text-gray-500 before:content-['“']"
          >
            <!-- eslint-disable vue/no-v-html -->
            <div v-html="review.text" />
          </div>
          <div
            v-if="review.avg"
            class="flex justify-center"
          >
            <YscRating
              :model-value="review.avg"
              small
              class="text-pink-500"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* safari: line-clamp does not work on not inline elements */
[class*='line-clamp'] p {
  @apply inline;
}
</style>
